import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import api from '../utils/api';

const AuthContext = createContext();

// Use relative paths so API calls always target the current domain
const API_URL = '';

const SESSION_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes for non-admin
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
const HTTP_UNAUTHORIZED = 401;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const timeoutRef = useRef(null);
  const isNonAdmin = useRef(false);

  const clearInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const performLogout = useCallback(async (expired = false) => {
    clearInactivityTimer();
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Server-side logout may fail if session already expired — non-critical
      process.env.NODE_ENV === 'development' && console.warn('Logout request failed (session may be expired):', err?.message);
    }
    isNonAdmin.current = false;
    setUser(false);
    if (expired) {
      setSessionExpired(true);
    }
  }, [clearInactivityTimer]);

  const resetInactivityTimer = useCallback(() => {
    if (!isNonAdmin.current) return;
    clearInactivityTimer();
    timeoutRef.current = setTimeout(() => {
      performLogout(true);
    }, SESSION_TIMEOUT_MS);
  }, [clearInactivityTimer, performLogout]);

  // Setup/teardown activity listeners when user changes
  useEffect(() => {
    if (user && user.role && user.role !== 'admin') {
      isNonAdmin.current = true;
      resetInactivityTimer();
      const handler = () => resetInactivityTimer();
      ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handler, { passive: true }));
      return () => {
        ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handler));
        clearInactivityTimer();
      };
    } else {
      isNonAdmin.current = false;
      clearInactivityTimer();
    }
  }, [user, resetInactivityTimer, clearInactivityTimer]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        // Session cookie expired or invalid — expected on fresh load
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for 401 events from the api interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      clearInactivityTimer();
      isNonAdmin.current = false;
      setUser(false);
      setSessionExpired(true);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [clearInactivityTimer]);

  const login = useCallback(async (user_id, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { user_id, password },
        { withCredentials: true }
      );
      // httpOnly cookies are set automatically by the browser from Set-Cookie response header
      setSessionExpired(false);
      setUser(data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Login failed. Please try again.';
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      
      return { success: false, error: errorMsg };
    }
  }, []);

  const logout = useCallback(async () => {
    await performLogout(false);
  }, [performLogout]);

  const switchUser = useCallback(async () => {
    await performLogout(false);
    setSessionExpired(false);
  }, [performLogout]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const authActions = useMemo(() => ({
    login, logout, switchUser, checkAuth, clearSessionExpired
  }), [login, logout, switchUser, checkAuth, clearSessionExpired]);

  const value = useMemo(() => ({
    user, loading, sessionExpired, ...authActions
  }), [user, loading, sessionExpired, authActions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
